import { Shield, Lock, Server, GitBranch, CheckCircle, Zap } from 'lucide-react'
import { motion } from 'framer-motion';
import StarCanvas from './StarCanvas';

const developmentMetrics = [
  {
    value: "90%",
    label: "Features Complete",
    icon: <CheckCircle className="w-6 h-6" />,
    description: "Core functionality ready"
  },
  {
    value: "Q2 2024",
    label: "Public Release",
    icon: <Zap className="w-6 h-6" />,
    description: "Join the waitlist now"
  },
  {
    value: "20",
    label: "Beta Spots",
    icon: <GitBranch className="w-6 h-6" />,
    description: "Early access positions"
  }
]

const featureProgress = [
  {
    name: "Lead Discovery Engine",
    progress: 95,
    status: "Ready for beta"
  },
  {
    name: "Autonomous Outreach",
    progress: 90,
    status: "Final testing"
  },
  {
    name: "AI Response System",
    progress: 85,
    status: "Optimization phase"
  }
]

const trustFeatures = [
  {
    icon: <Shield className="w-6 h-6" />,
    title: "End-to-End Solution",
    description: "Complete sales automation with no integrations needed"
  },
  {
    icon: <Lock className="w-6 h-6" />,
    title: "Autonomous AI",
    description: "Intelligent decision-making at every step"
  },
  {
    icon: <Server className="w-6 h-6" />,
    title: "Enterprise Ready",
    description: "Built for scale with robust security"
  }
]

const integrations = [
  "Salesforce", "HubSpot", "Outreach", "SalesLoft", 
  "LinkedIn Sales Nav", "Apollo", "ZoomInfo"
]

// Stat Card Component
const StatCard = ({ icon, value, label, description }: { 
  icon: React.ReactNode;
  value: string;
  label: string;
  description: string;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    whileHover={{ scale: 1.02 }}
    className="relative group"
  >
    <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-purple-400/20 rounded-xl blur-xl group-hover:opacity-100 opacity-0 transition-opacity" />
    <div className="relative bg-black/40 rounded-xl p-8 border border-purple-500/10 group-hover:border-purple-500/20 transition-colors">
      <motion.div 
        className="text-3xl mb-4 text-purple-400"
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        {icon}
      </motion.div>
      <motion.div 
        className="text-3xl font-bold text-white mb-2"
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        {value}
      </motion.div>
      <div className="text-purple-400 mb-1">{label}</div>
      <div className="text-gray-500 text-sm">{description}</div>
    </div>
  </motion.div>
);

// Progress Bar Component
const ProgressBar = ({ name, progress, status }: {
  name: string;
  progress: number;
  status: string;
}) => (
  <div className="space-y-2">
    <div className="flex justify-between items-center">
      <span className="text-white">{name}</span>
      <span className="text-purple-400 font-semibold">
        {progress}%
      </span>
    </div>
    <div className="h-2 bg-black/50 rounded-full overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        whileInView={{ width: `${progress}%` }}
        viewport={{ once: true }}
        transition={{ duration: 1, delay: 0.2 }}
        className="h-full bg-gradient-to-r from-purple-600 via-purple-500 to-purple-400 rounded-full"
      />
    </div>
    <div className="text-gray-500 text-sm">{status}</div>
  </div>
);

// Feature Card Component
const FeatureCard = ({ icon, title, description, delay }: {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay }}
    whileHover={{ scale: 1.02 }}
    className="relative group"
  >
    <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-purple-400/10 rounded-xl blur-xl group-hover:opacity-100 opacity-0 transition-opacity" />
    <div className="relative bg-black/40 rounded-xl p-8 border border-purple-500/10 group-hover:border-purple-500/20 transition-colors">
      <motion.div 
        className="text-3xl mb-4 text-purple-400"
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        {icon}
      </motion.div>
      <h3 className="text-xl font-semibold text-white mb-3">{title}</h3>
      <p className="text-gray-400">{description}</p>
    </div>
  </motion.div>
);

export const TrustBuilding = () => {
  return (
    <section className="relative py-24 overflow-hidden bg-black">
      {/* Background Effects */}
      <StarCanvas />
      
      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:14px_24px] opacity-50" />
      
      <div className="max-w-6xl mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Development Status</h2>
          <p className="text-gray-400 text-xl max-w-2xl mx-auto mb-16">
            ACOS prototype is ready for early users
          </p>

          {/* Development Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
            {developmentMetrics.map((metric, index) => (
              <StatCard
                key={index}
                icon={metric.icon}
                value={metric.value}
                label={metric.label}
                description={metric.description}
              />
            ))}
          </div>

          {/* Feature Progress */}
          <div className="grid grid-cols-1 gap-8 mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-black/40 backdrop-blur-sm rounded-xl p-8 border border-purple-500/10"
            >
              <h3 className="text-xl font-semibold text-white mb-8">Feature Completion Status</h3>
              <div className="grid gap-6">
                {featureProgress.map((feature, index) => (
                  <ProgressBar
                    key={index}
                    name={feature.name}
                    progress={feature.progress}
                    status={feature.status}
                  />
                ))}
              </div>
            </motion.div>
          </div>

          {/* Trust Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
            {trustFeatures.map((feature, index) => (
              <FeatureCard
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                delay={index * 0.2}
              />
            ))}
          </div>


        </motion.div>
      </div>
    </section>
  )
} 